import { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import { MoveInOutInspectionListTable } from './Components/MoveInOutInspectionListTable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateMoveInOutInspectionModal } from './Components/GenerateMoveInMoveOutInspectionModal';
import { DeleteMoveInOutInspectionConfirmationModal } from './Components/DeleteMoveInMoveOutInspectionConfirmationModal';

import {
  defaultMoveInOutInspection,
  getMoveInOutInspectionInitialValues,
  autoFillPropertyDetails,
} from './Components/FormikHelpers';

import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';

import {
  createMoveInOutInspection,
  getMoveInOutInspectionList,
  updateMoveInOutInspection,
  deleteMoveInOutInspection,
  sendMoveInOutInspectionReport,
  downloadMoveInOutInspectionBundle,
} from './Components/FormikHelpers/Api';

import { moveInOutInspectionSchema } from './Components/FormikHelpers/ValidationSchema';
import { setLoading } from '../../../../store/actions';

const GenerateMoveInOutInspection = () => {
  const dispatch = useDispatch();

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [properties, setProperties] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState({
    property_id: '',
    building_name: '',
    unit_number: '',
    inspection_type: 'all',
    date_from: '',
    date_to: '',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;

    setUser(currentUser);

    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchInspections = useCallback(
    async (filters) => {
      try {
        dispatch(setLoading(true));

        const params = {
          propertyId: filters.property_id || undefined,
          unit_number: filters.unit_number || undefined,
          type: filters.inspection_type === 'all' ? undefined : filters.inspection_type,
          dateFrom: filters.date_from || undefined,
          dateTo: filters.date_to || undefined,
        };

        const response = await getMoveInOutInspectionList(params);
        setInspections(response?.data || response || []);
      } catch (error) {
        console.error('Failed to fetch inspections:', error);
        setInspections([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  const handleSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      try {
        dispatch(setLoading(true));
        await moveInOutInspectionSchema.validate(values, { abortEarly: false });

        const payload = {
          ...values,
          inspection_items: values.inspection_items.map((item) => ({
            ...item,
            move_in_condition: {
              ...item.move_in_condition,
              pictures:
                item.move_in_condition?.pictures?.map((p) =>
                  typeof p === 'string' ? p : p.url || p.fileUrl || p
                ) || [],
            },
            move_out_condition: {
              ...item.move_out_condition,
              pictures:
                item.move_out_condition?.pictures?.map((p) =>
                  typeof p === 'string' ? p : p.url || p.fileUrl || p
                ) || [],
            },
          })),
        };

        let inspectionId;

        if (selectedInspection?._id) {
          await updateMoveInOutInspection(selectedInspection._id, payload);
          inspectionId = selectedInspection._id;
        } else {
          const createResponse = await createMoveInOutInspection(payload);
          inspectionId = createResponse?._id || createResponse?.data?._id;
        }

        if (inspectionId) {
          try {
            const sendResponse = await sendMoveInOutInspectionReport(inspectionId, {});
            const { sentTo = [], filename } = sendResponse || {};

            const recipientText =
              sentTo.length > 0
                ? `to ${sentTo.length} recipient${sentTo.length === 1 ? '' : 's'}`
                : 'to default recipients (tenant/landlord)';

            Swal.fire({
              icon: 'success',
              title: 'Report Sent Automatically',
              html: `
                The inspection report has been sent successfully<br>
                <small>${recipientText}</small><br>
                <small style="color: #666;">File: ${filename || 'inspection-report.pdf'}</small>
              `,
              timer: 5000,
              timerProgressBar: true,
            });
          } catch (emailErr) {
            console.error('Auto-email failed:', emailErr);
            Swal.fire({
              icon: 'warning',
              title: 'Report Saved, but Email Failed',
              text: 'The inspection was saved/updated successfully, but we couldn’t send the email automatically. You can send it manually from the table.',
              confirmButtonText: 'OK',
            });
          }
        }

        resetForm({ values: defaultMoveInOutInspection });
        setSelectedInspection(null);
        setIsModalOpen(false);

        fetchInspections(selectedFilters);
      } catch (err) {
        if (err.name === 'ValidationError') {
          const errors = err.inner.map((e) => ({
            path: e.path || 'Field',
            message: e.message,
          }));

          let html = '<div style="text-align:left; max-height:400px; overflow-y:auto;">';
          errors.forEach(({ path, message }) => {
            html += `<div><strong>${path}:</strong> ${message}</div>`;
          });
          html += '</div>';

          Swal.fire({
            icon: 'warning',
            title: 'Please fix the following issues',
            html,
            width: 720,
          });
        } else {
          Swal.fire('Error', err.message || 'Failed to save inspection', 'error');
        }
      } finally {
        setSubmitting(false);
        dispatch(setLoading(false));
      }
    },
    [selectedInspection, selectedFilters, fetchInspections, dispatch]
  );

  const formik = useFormik({
    initialValues: defaultMoveInOutInspection,
    validationSchema: moveInOutInspectionSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleAddClick = async () => {
    if (!selectedFilters.property_id) {
      Swal.fire('Warning', 'Please select a property first.', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: 'Start New Move-In/Out Inspection',
      icon: 'question',
      html: `
      <div style="text-align: left; font-size: 15px; margin: 20px 0; line-height: 1.6;">
        <p><strong>Who is participating in this walkthrough?</strong></p>
        
        <div style="margin: 16px 0; padding: 12px; border: 1px solid #e2e8f0; border-radius: 6px;">
          <strong>Move-In Inspection</strong><br>
          <label style="display:block; margin: 10px 0 10px 24px; cursor: pointer;">
            <input type="radio" name="phase" value="1" checked style="margin-right: 8px;">
            Landlord + Tenant (joint walkthrough)
          </label>
          <label style="display:block; margin: 10px 0 10px 24px; cursor: pointer;">
            <input type="radio" name="phase" value="2" style="margin-right: 8px;">
            Only Landlord (no tenant present)
          </label>
        </div>

        <div style="margin: 16px 0; padding: 12px; border: 1px solid #e2e8f0; border-radius: 6px;">
          <strong>Move-Out Inspection</strong><br>
          <label style="display:block; margin: 10px 0 10px 24px; cursor: pointer;">
            <input type="radio" name="phase" value="4" style="margin-right: 8px;">
            Landlord + Tenant (final walkthrough together)
          </label>
          <label style="display:block; margin: 10px 0 10px 24px; cursor: pointer;">
            <input type="radio" name="phase" value="3" style="margin-right: 8px;">
            Only Landlord (tenant already vacated)
          </label>
        </div>
      </div>
    `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Continue',
      confirmButtonColor: '#3085d6',
      allowOutsideClick: false,
      preConfirm: () => {
        const selected = document.querySelector('input[name="phase"]:checked');
        if (!selected) {
          Swal.showValidationMessage('Please select an option');
          return false;
        }
        return Number(selected.value);
      },
    });

    if (!result.isConfirmed) return;

    const selectedPhase = result.value;
    let confirmResult;
    if (selectedPhase === 1) {
      confirmResult = await Swal.fire({
        title: 'Confirm Tenant Presence',
        html: `
        <div style="text-align: left; font-size: 15px;">
          <p>You selected <strong>Landlord + Tenant</strong> for Move-In.</p>
          <p style="margin-top: 12px;">
            Are you sure a <strong>tenant is present</strong> with you right now for this inspection?
          </p>
        </div>
      `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Tenant is Present',
        cancelButtonText: 'No, Go Back',
        reverseButtons: true,
        confirmButtonColor: '#3085d6',
      });
    } else if (selectedPhase === 2) {
      confirmResult = await Swal.fire({
        title: 'Confirm No Tenant',
        html: `
        <div style="text-align: left; font-size: 15px;">
          <p>You selected <strong>Only Landlord</strong> for Move-In.</p>
          <p style="margin-top: 12px; color: #e53e3e; font-weight: 500;">
            Are you sure the <strong>tenant is NOT present</strong> and not participating in this inspection?
          </p>
          <p style="margin-top: 12px;">
            Tenant-related fields will be skipped.
          </p>
        </div>
      `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Proceed (No Tenant)',
        cancelButtonText: 'Go Back',
        reverseButtons: true,
        confirmButtonColor: '#d33',
      });
    } else if (selectedPhase === 4) {
      confirmResult = await Swal.fire({
        title: 'Confirm Tenant Presence',
        html: `
        <div style="text-align: left; font-size: 15px;">
          <p>You selected <strong>Landlord + Tenant</strong> for Move-Out (final walkthrough).</p>
          <p style="margin-top: 12px;">
            Are you sure a <strong>tenant is present</strong> with you right now for this final inspection?
          </p>
        </div>
      `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Tenant is Present',
        cancelButtonText: 'No, Go Back',
        reverseButtons: true,
        confirmButtonColor: '#3085d6',
      });
    } else if (selectedPhase === 3) {
      confirmResult = await Swal.fire({
        title: 'Confirm Tenant Vacated',
        html: `
        <div style="text-align: left; font-size: 15px;">
          <p>You selected <strong>Only Landlord</strong> for Move-Out.</p>
          <p style="margin-top: 12px; color: #e53e3e; font-weight: 500;">
            Are you sure the <strong>tenant has already vacated</strong> and is not participating?
          </p>
          <p style="margin-top: 12px;">
            Tenant-related fields will be skipped.
          </p>
        </div>
      `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Proceed (Tenant Vacated)',
        cancelButtonText: 'Go Back',
        reverseButtons: true,
        confirmButtonColor: '#d33',
      });
    }

    if (!confirmResult?.isConfirmed) return;
    try {
      setIsAutoFilling(true);
      setSelectedInspection(null);

      const autoFilled = await autoFillPropertyDetails(
        defaultMoveInOutInspection,
        selectedFilters.property_id,
        selectedFilters.building_name,
        selectedFilters.unit_number,
        selectedPhase
      );

      autoFilled.current_phase = selectedPhase;

      formik.resetForm({ values: autoFilled });
      setIsModalOpen(true);
    } catch (error) {
      console.error('Auto-fill failed:', error);
      Swal.fire('Error', 'Failed to prepare new inspection', 'error');
    } finally {
      setIsAutoFilling(false);
    }
  };

  const handleEditClick = async (inspection) => {
    if (!inspection?._id) return;

    setSelectedInspection(inspection);
    setIsPreviewMode(false);

    try {
      setIsAutoFilling(true);
      const values = await getMoveInOutInspectionInitialValues(inspection._id);

      const hasSignatures = (section) => {
        if (!section) return false;
        return !!(
          section['1']?.landlord_signature ||
          section['2']?.a?.tenant_signature ||
          section['2']?.b?.tenant_signature ||
          section['2']?.c?.landlord_refusal_signature ||
          section['3']?.landlord_signature
        );
      };

      const moveInSigned = hasSignatures(values.move_in);
      const moveOutSigned = hasSignatures(values.move_out);

      // Fully signed → preview only
      if (moveInSigned && moveOutSigned) {
        formik.setValues({ ...values, _id: inspection._id });
        setIsPreviewMode(true);
        setIsModalOpen(true);
        return;
      }

      let phaseOptions = [];

      if (!moveInSigned) {
        phaseOptions.push(
          { value: 1, label: 'Move-In: Landlord + Tenant', phase: 'move_in' },
          { value: 2, label: 'Move-In: Only Landlord', phase: 'move_in' }
        );
      }
      if (!moveOutSigned) {
        phaseOptions.push(
          { value: 3, label: 'Move-Out: Landlord + Tenant', phase: 'move_out' },
          { value: 4, label: 'Move-Out: Only Landlord', phase: 'move_out' }
        );
      }

      const result = await Swal.fire({
        title: 'Continue Inspection',
        icon: 'question',
        html: `
        <div style="text-align:left; font-size:15px;">
          <p><strong>Which phase do you want to work on?</strong></p>
          ${phaseOptions.map(opt => `
            <label style="display:block; margin:10px 0 10px 24px; cursor:pointer;">
              <input type="radio" name="edit_phase" value="${opt.value}" style="margin-right:8px;">
              ${opt.label}
            </label>
          `).join('')}
        </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        preConfirm: () => {
          const selected = document.querySelector('input[name="edit_phase"]:checked');
          if (!selected) {
            Swal.showValidationMessage('Please select a phase');
            return false;
          }
          return Number(selected.value);
        },
      });

      if (!result.isConfirmed) return;

      const selectedPhase = result.value;

      const autoFilledValues = await autoFillPropertyDetails(
        values,
        values.property_details?.property_id,
        values.property_details?.unit_number,
        selectedPhase
      );

      const updatedValues = {
        ...autoFilledValues,
        _id: inspection._id,
        current_phase: selectedPhase,
        inspection_items: values.inspection_items || [],
        keys: values.keys || [],
        conclusions_recommendations: values.conclusions_recommendations || ''
      };

      formik.setValues(updatedValues);
      setIsPreviewMode(false);
      setIsModalOpen(true);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to load inspection details', 'error');
    } finally {
      setIsAutoFilling(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInspection?._id) return;

    try {
      dispatch(setLoading(true));
      await deleteMoveInOutInspection(selectedInspection._id);
      Swal.fire('Deleted', 'Inspection deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedInspection(null);
      fetchInspections(selectedFilters);
    } catch (error) {
      Swal.fire('Error', 'Failed to delete inspection', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSendInspectionEmail = async (inspection) => {
    if (!inspection?._id) {
      Swal.fire('Error', 'No inspection selected', 'error');
      return;
    }

    let emails = [''];

    const { value: confirmed, isConfirmed } = await Swal.fire({
      title: 'Send Move-In/Out Inspection Report',
      html: `
      <div style="text-align: left; margin-bottom: 16px;">
        <p>Enter email addresses to send the report to:</p>
      </div>
      <div id="email-container" style="max-height: 300px; overflow-y: auto; padding-right: 8px;">
        <!-- Email fields will be added here dynamically -->
      </div>
      <button id="add-email-btn" class="swal2-confirm swal2-styled" style="margin-top: 12px;">
        + Add another email
      </button>
      <div style="margin-top: 16px; font-size: 0.9em; color: #666;">
        The report will also be sent to default recipients unless you add at least one email.
      </div>
    `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Send Report',
      cancelButtonText: 'Cancel',
      willOpen: () => {
        const container = document.getElementById('email-container');
        const addBtn = document.getElementById('add-email-btn');

        const renderEmails = () => {
          container.innerHTML = '';

          emails.forEach((email, index) => {
            const row = document.createElement('div');
            row.style.display = 'flex';
            row.style.alignItems = 'center';
            row.style.marginBottom = '10px';
            row.style.gap = '8px';
            const input = document.createElement('input');
            input.type = 'email';
            input.value = email;
            input.placeholder = `Email ${index + 1}`;
            input.style.flex = '1';
            input.style.padding = '10px';
            input.style.borderRadius = '6px';
            input.style.border = '1px solid #ced4da';
            input.oninput = (e) => {
              emails[index] = e.target.value.trim();
            };
            row.appendChild(input);
            if (emails.length > 1) {
              const removeBtn = document.createElement('button');
              removeBtn.innerHTML = '×';
              removeBtn.style.width = '32px';
              removeBtn.style.height = '32px';
              removeBtn.style.borderRadius = '50%';
              removeBtn.style.border = 'none';
              removeBtn.style.background = '#dc3545';
              removeBtn.style.color = 'white';
              removeBtn.style.fontSize = '16px';
              removeBtn.style.cursor = 'pointer';
              removeBtn.title = 'Remove this email';

              removeBtn.onclick = () => {
                emails.splice(index, 1);
                renderEmails();
              };

              row.appendChild(removeBtn);
            }

            container.appendChild(row);
          });
        };

        renderEmails();

        addBtn.onclick = () => {
          emails.push('');
          renderEmails();
          setTimeout(() => {
            const inputs = container.querySelectorAll('input');
            if (inputs.length > 0) inputs[inputs.length - 1].focus();
          }, 50);
        };
      },
      preConfirm: () => {
        const validEmails = emails
          .map(e => e.trim())
          .filter(e => e && e.includes('@') && e.includes('.'));

        if (validEmails.length === 0 && emails.some(e => e.trim() !== '')) {
          Swal.showValidationMessage('Please enter valid email addresses or clear invalid ones.');
          return false;
        }

        return { emails: validEmails };
      },
    });

    if (!isConfirmed) return;

    try {
      dispatch(setLoading(true));
      const sendPayload = confirmed?.emails?.length > 0
        ? { emails: confirmed.emails }
        : {};

      const response = await sendMoveInOutInspectionReport(inspection._id, sendPayload);

      const { sentTo = [], filename } = response || {};

      const recipientText =
        sentTo.length > 0
          ? `to ${sentTo.length} recipient${sentTo.length === 1 ? '' : 's'}`
          : 'to default recipients';

      Swal.fire({
        icon: 'success',
        title: 'Report Sent!',
        html: `
        Inspection report has been sent successfully<br>
        <small>${recipientText}</small><br>
        <small style="color: #666;">File: ${filename || 'report.pdf'}</small>
      `,
        timer: 4000,
      });

      fetchInspections(selectedFilters);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Send',
        text: err?.message || err?.response?.data?.message || 'Could not send the report. Please try again.',
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFiltersChange = useCallback((newFilters) => {
    setSelectedFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleDownloadBundle = useCallback(async (inspection) => {
    try {
      dispatch(setLoading(true));
      await downloadMoveInOutInspectionBundle(inspection._id);
      Swal.fire({ icon: 'success', title: 'Download started', text: 'Your inspection PDF and media ZIP is being downloaded.', timer: 2500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Download failed', text: err?.message || 'Could not create the inspection ZIP. Please try again.' });
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchInspections(selectedFilters);
  }, [selectedFilters, fetchInspections]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Move-In/Out Inspections</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Move-In/Out Inspections" />

        <PropertySelector
          propertyList={properties}
          selectedFilters={selectedFilters}
          onFiltersChange={handleFiltersChange}
          onAddClick={handleAddClick}
          isLoading={isAutoFilling}
          user={user}
          showUnitFilter={true}
        />

        <MoveInOutInspectionListTable
          items={inspections}
          onDelete={(inspection) => {
            setSelectedInspection(inspection);
            setIsDeleteModalOpen(true);
          }}
          onEdit={handleEditClick}
          onSendEmail={handleSendInspectionEmail}
          onDownload={handleDownloadBundle}
          onRefresh={() => fetchInspections(selectedFilters)}
          userRole={user?.role || 'other'}
          userId={user?.id || ''}
        />

        <GenerateMoveInOutInspectionModal
          formik={formik}
          filters={selectedFilters}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isEdit={!!selectedInspection}
          onRefresh={() => fetchInspections(selectedFilters)}
          currentPhase={formik.values.current_phase}
          previewMode={isPreviewMode}
          onSaveSuccess={() => {
            setSelectedFilters({
              property_id: selectedFilters.property_id,
              building_name: '',
              unit_number: '',
              inspection_type: 'all',
              date_from: '',
              date_to: '',
            });
            fetchInspections({
              ...selectedFilters,
              building_name: '',
              unit_number: '',
              inspection_type: 'all',
              date_from: '',
              date_to: '',
            });
          }}
        />

        <DeleteMoveInOutInspectionConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          inspectionLog={selectedInspection}
        />
      </div>
    </div>
  );
};

export { GenerateMoveInOutInspection };
